/**
 * Central keyboard-shortcut registry — the single source of truth for every
 * user-facing shortcut's keys, label, and grouping.
 *
 * Both shortcut *registration* (`useShortcut`, which looks up keys by id) and
 * the *overlay* (which renders the grouped list) read from here, so the two can
 * never drift. To add or change a shortcut you edit this file once; the call
 * site only references the stable `id`.
 *
 * Key syntax is react-hotkeys-hook's: `"mod+k"`, `"shift+slash"`, `"d"`,
 * `"up"`. Use `"mod"` (not `"meta"`) for the cross-platform ⌘/Ctrl modifier so
 * Windows users get Ctrl; `useShortcut` expands it to the one modifier this
 * platform uses (react-hotkeys-hook's own `mod` matches ⌘ and Ctrl alike, which
 * would steal macOS's Ctrl-key text-editing bindings — see `platform.ts`). A
 * space-separated combo like `"g c"` denotes a Gmail-style leader sequence (g,
 * then c) — handled by the sequence layer, not react-hotkeys-hook.
 *
 * Gating is derived per-key by `useShortcut`: combos with a real modifier
 * (mod/ctrl/alt) are always active and fire while typing; bare single keys and
 * leader sequences are gated behind the user's "shortcuts enabled" preference
 * and suppressed while a text field is focused. `alwaysOn` overrides the gate
 * for bare keys that must always work (e.g. `?` to open this very overlay).
 * A literal `ctrl` combo (as opposed to `mod`) is always active but, on macOS,
 * does not fire while typing: there Ctrl+letter is text navigation (Ctrl+N is
 * next line), so a text field keeps it.
 *
 * The `keys` here are defaults. A user can store their own list per id under
 * the `keyboard.bindings` preference (see `bindings.ts`), and gating follows
 * the keys actually bound, so a shortcut moved from `e` to `alt+e` starts
 * firing inside the composer. Ids are shared with the server through
 * `@/types/keyboardShortcuts.ts`, which is what makes them preference keys.
 */

import { type KeyboardShortcutId, keyboardShortcutIds } from "@/types/keyboardShortcuts.ts";
import { recordOf } from "@/types/utils.ts";

export type ShortcutGroupId =
  | "agent"
  | "navigation"
  | "calendar"
  | "threads"
  | "composer"
  | "event"
  | "system";

export interface ShortcutDef {
  /** Stable identifier; call sites register a handler via `useShortcut(id, …)`. */
  id: KeyboardShortcutId;
  /**
   * Key combos, primary first (shown first in the overlay), alternates after.
   * Each is react-hotkeys-hook syntax; a space-separated combo is a leader
   * sequence. Multiple combos = the same action bound to several keys.
   */
  keys: string[];
  /** Human-readable action label for the overlay. */
  label: string;
  /** Fire even when the "shortcuts enabled" preference is off (bare keys only). */
  alwaysOn?: boolean;
  /**
   * Let bare keys fire while a text field is focused. Default false (so typing
   * `j` doesn't navigate). Set for control keys like Escape that should still
   * work mid-edit. Modifier combos fire in inputs regardless, except `ctrl`.
   */
  inInput?: boolean;
  /** Hidden from the overlay (internal/debug shortcuts that still want registry gating). */
  hidden?: boolean;
  /**
   * Not customizable: the handler is hand-wired rather than registered through
   * `useShortcut`, so a stored binding would show in the editor and do nothing.
   * The entry exists for the overlay.
   */
  fixed?: boolean;
}

interface ShortcutGroupDef {
  id: ShortcutGroupId;
  /** Section heading in the overlay. */
  title: string;
  shortcuts: ShortcutDef[];
}

/**
 * Ordered groups — also the section order in the overlay. Only shortcuts that
 * are actually wired up belong here; an entry with no `useShortcut` call site
 * would show in the overlay but do nothing.
 */
export const SHORTCUT_GROUPS: ShortcutGroupDef[] = [
  {
    id: "agent",
    title: "Agent",
    shortcuts: [
      {
        id: "agent.open",
        // ⌘K is the agent (per product direction — push agent usage), with the
        // familiar new-thread single keys as alternates. Bare `space` is safe
        // here ONLY because the calendar surface doesn't document-scroll: with
        // no focused space-consumer (see useShortcut's guard), space's dominant
        // meaning is page-down, which useShortcut preventDefault()s. Before
        // binding agent.open on a scrolling surface (a thread view, settings,
        // long home feed) reconsider `space`.
        //
        // `ctrl+n` is spelled with a literal Ctrl rather than `mod` because it
        // exists for Windows, where ⌘ doesn't — it must not become ⌘N on a Mac.
        keys: ["mod+k", "mod+slash", "ctrl+n", "c", "n", "/", "space"],
        label: "Ask the agent / new thread",
      },
    ],
  },
  {
    id: "navigation",
    title: "Go to",
    shortcuts: [
      { id: "nav.calendar", keys: ["g c"], label: "Calendar" },
      { id: "nav.threads", keys: ["g t"], label: "Threads" },
      { id: "nav.routines", keys: ["g r"], label: "Routines" },
      { id: "nav.skills", keys: ["g s"], label: "Skills" },
      { id: "nav.memories", keys: ["g m"], label: "Memories" },
      { id: "nav.contacts", keys: ["g p"], label: "People" },
      { id: "nav.settings", keys: ["g comma"], label: "Settings" },
    ],
  },
  {
    id: "calendar",
    title: "Calendar",
    shortcuts: [
      { id: "calendar.viewDay", keys: ["d", "1"], label: "Day view" },
      { id: "calendar.viewWeek", keys: ["w", "2"], label: "Week view" },
      { id: "calendar.viewMonth", keys: ["m", "3"], label: "Month view" },
      { id: "calendar.viewMultiDay", keys: ["x", "4"], label: "Multi-day view" },
      { id: "calendar.today", keys: ["t"], label: "Go to today" },
      { id: "calendar.prev", keys: ["k", "left"], label: "Previous period" },
      { id: "calendar.next", keys: ["j", "right"], label: "Next period" },
    ],
  },
  {
    id: "threads",
    title: "Threads",
    shortcuts: [
      { id: "threads.prev", keys: ["k", "up"], label: "Previous thread" },
      { id: "threads.next", keys: ["j", "down"], label: "Next thread" },
      { id: "threads.open", keys: ["enter", "o"], label: "Open thread" },
      { id: "threads.back", keys: ["u"], label: "Back to list" },
      { id: "threads.archive", keys: ["e", "mod+d"], label: "Archive / mark done" },
    ],
  },
  {
    id: "composer",
    title: "Composer",
    // Bound inside the composer's own key handler, not through `useShortcut`.
    shortcuts: [{ id: "composer.send", keys: ["mod+enter"], label: "Send message", fixed: true }],
  },
  {
    id: "system",
    title: "App",
    shortcuts: [
      {
        id: "system.help",
        keys: ["shift+slash"],
        label: "Show keyboard shortcuts",
        alwaysOn: true,
      },
      { id: "system.toggleSidebar", keys: ["mod+b"], label: "Toggle chat panel" },
      { id: "system.sync", keys: ["mod+i"], label: "Sync" },
      { id: "system.back", keys: ["escape"], label: "Go back", inInput: true },
    ],
  },
];

/** Every shortcut, in overlay order. */
export const ALL_SHORTCUTS: ShortcutDef[] = SHORTCUT_GROUPS.flatMap((group) => group.shortcuts);

const entryById = new Map(
  SHORTCUT_GROUPS.flatMap((group) =>
    group.shortcuts.map(
      (def): [KeyboardShortcutId, { def: ShortcutDef; group: ShortcutGroupId }] => [
        def.id,
        { def, group: group.id },
      ],
    ),
  ),
);

// Every id in the shared list must have a registry entry; `shortcuts.test.ts`
// pins the two lists to each other, so this only fires on a half-done edit.
const entryFor = (id: KeyboardShortcutId) => {
  const entry = entryById.get(id);
  if (!entry) throw new Error(`Unknown keyboard shortcut id: "${id}"`);
  return entry;
};

/** Flat lookup by id, derived from the groups. */
export const SHORTCUTS: Record<KeyboardShortcutId, ShortcutDef> = recordOf(
  keyboardShortcutIds,
  (id) => entryFor(id).def,
);

/** The group a shortcut belongs to. */
export const SHORTCUT_GROUP_OF: Record<KeyboardShortcutId, ShortcutGroupId> = recordOf(
  keyboardShortcutIds,
  (id) => entryFor(id).group,
);

export const getShortcut = (id: KeyboardShortcutId): ShortcutDef => entryFor(id).def;
